import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Toast } from '../../../shared/components';
import { ArrowLeft, RefreshCw, Settings } from 'lucide-react';
import { useEventStats, useRoles } from '../../../shared/hooks';
import { RoleProtectedRoute } from '../../../shared/components';
import { adminService } from '../../../shared/services';
import SeatMap from '../../theater/SeatMap';
import { useSeats } from '../../theater/useSeats';
import SeatConfigModal from './SeatConfigModal';

const EventDetails = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { isAdmin, ROLES } = useRoles();
  
  // Estados del mapa
  const [loading, setLoading] = useState(true);
  const [eventData, setEventData] = useState(null);
  
  // Estado del modal de configuración de asientos
  const [isSeatConfigModalOpen, setIsSeatConfigModalOpen] = useState(false);

  // Hook para estadísticas de eventos
  const { stats, loading: statsLoading, loadEventStats } = useEventStats();

  // Hook para obtener los asientos del evento
  const {
    plantaBajaSeats,
    primerPisoSeats,
    allSeats,
    loadingOccupiedSeats,
    getSeatClassName,
    loadOccupiedSeats,
    seatTypeList,
    seatTypePriceMap,
    refetch: refetchSeatTypes
  } = useSeats(6, eventId, [], true); // true = viewMode

  // Estados de notificaciones
  const [toast, setToast] = useState({
    isVisible: false,
    message: '',
    type: 'success'
  });

  const loadEventData = useCallback(async () => {
    setLoading(true);
    try {
      const eventData = await adminService.getEventById(eventId);
      setEventData(eventData);
    } catch (error) {
      showToast('Error al cargar los datos del evento', 'error');
      // Usar datos de ejemplo como fallback
      setEventData({
        id: eventId,
        title: 'Evento de Prueba',
        eventDate: '2024-01-15',
        eventTime: '20:00',
        venue: 'Teatro Principal'
      });
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  // Cargar datos del evento
  useEffect(() => {
    if (eventId) {
      loadEventData();
      loadEventStats(eventId);
    }
  }, [eventId, loadEventStats, loadEventData]);





  // Los controles de zoom están manejados internamente por el componente SeatMap

  // Refrescar datos
  const handleRefresh = async () => {
    await Promise.all([
      loadEventStats(eventId),
      loadOccupiedSeats()
    ]);
    showToast('Datos actualizados', 'success');
  };

  // Mostrar notificación
  const showToast = (message, type = 'success') => {
    setToast({
      isVisible: true,
      message,
      type
    });
  };

  // Ocultar notificación
  const hideToast = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  // Función para formatear fecha de YYYY-MM-DD a DD-MM-YYYY
  const formatDateToDDMMYYYY = (dateString) => {
    if (!dateString) return '';
    
    try {
      const [year, month, day] = dateString.split('-');
      return `${day}-${month}-${year}`;
    } catch (error) {
      return dateString; // Retorna la fecha original si hay error
    }
  };

  if (loading || loadingOccupiedSeats) {
    return (
      <RoleProtectedRoute requiredRoles={[ROLES.ADMIN, ROLES.USER]}>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">
              {loading ? 'Cargando detalles del evento...' : 'Cargando estado de asientos...'}
            </p>
          </div>
        </div>
      </RoleProtectedRoute>
    );
  }

  return (
    <RoleProtectedRoute requiredRoles={[ROLES.ADMIN, ROLES.USER]}>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between py-4">
              <div className="flex items-center space-x-4">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/admin/events')}
                  className="text-gray-600 hover:text-gray-900"
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  Volver a Eventos
                </Button>
              </div>
              
              <div className="flex items-center space-x-2">
                {/* Botón de Configurar Asientos - Solo visible para administradores */}
                {isAdmin && (
                  <Button
                    variant="outline"
                    onClick={() => setIsSeatConfigModalOpen(true)}
                    className="text-purple-600 hover:text-purple-800 border-purple-300 hover:border-purple-400"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Configurar Asientos
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={handleRefresh}
                  disabled={statsLoading}
                  className="text-gray-600 hover:text-gray-900"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${statsLoading ? 'animate-spin' : ''}`} />
                  Actualizar
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Contenido principal */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Mapa del teatro - 3 columnas */}
            <div className="lg:col-span-3">
              <Card className="h-full">
                <CardHeader>
                  <div>
                    <CardTitle>Plano del Teatro</CardTitle>
                    <CardDescription>
                      Vista del estado actual de los asientos
                    </CardDescription>
                  </div>
                </CardHeader>
                
                <CardContent className="p-0">
                  <SeatMap
                    allSeats={allSeats}
                    plantaBajaSeats={plantaBajaSeats}
                    primerPisoSeats={primerPisoSeats}
                    handleSeatClick={() => {}} // Función vacía para deshabilitar clicks
                    getSeatClassName={getSeatClassName}
                    selectedSeats={[]} // No hay selección en modo admin
                    reservationsToCancel={[]} // No hay cancelaciones en modo admin
                    seatTypeList={seatTypeList}
                    seatTypePriceMap={seatTypePriceMap}
                  />
                </CardContent>
              </Card>
            </div>

            {/* Panel lateral - 2 columnas */}
            <div className="lg:col-span-2 space-y-6">
              {/* Resumen de entradas */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    📊 Resumen de Entradas
                  </CardTitle>
                  <CardDescription>
                    Estado actual de las reservas
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Estadísticas principales */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="text-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="text-2xl font-bold text-gray-600">{stats.blockedTickets}</div>
                      <div className="text-xs text-gray-800">Bloqueadas</div>
                    </div>
                    
                    <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="text-2xl font-bold text-blue-600">{stats.reservedTickets}</div>
                      <div className="text-xs text-blue-800">Reservadas</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-3">
                    <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="text-2xl font-bold text-green-600">{stats.soldTickets}</div>
                      <div className="text-xs text-green-800">Vendidas</div>
                    </div>
                  </div>

                  <div className="text-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="text-2xl font-bold text-gray-600">{stats.availableTickets}</div>
                    <div className="text-xs text-gray-800">Disponibles</div>
                  </div>

                  {/* Barra de ocupación */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Ocupación</span>
                      <span className="font-medium">
                        {stats.totalTickets > 0 ? (((stats.soldTickets + stats.reservedTickets) / stats.totalTickets) * 100).toFixed(1) : 0}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-green-500 to-blue-500 h-2 rounded-full transition-all duration-300"
                        style={{ 
                          width: `${Math.min(stats.totalTickets > 0 ? ((stats.soldTickets + stats.reservedTickets) / stats.totalTickets) * 100 : 0, 100)}%` 
                        }}
                      ></div>
                    </div>
                  </div>

                  {/* Total */}
                  <div className="border-t pt-3">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-sm">Total:</span>
                      <span className="font-bold text-lg">{stats.totalTickets}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Información del evento */}
              <Card>
                <CardHeader>
                  <CardTitle>Información del Evento</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-500">Título</label>
                    <p className="text-sm font-medium">{eventData?.title}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Fecha</label>
                    <p className="text-sm font-medium">{formatDateToDDMMYYYY(eventData?.eventDate)}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Hora</label>
                    <p className="text-sm font-medium">{eventData?.eventTime}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Teatro</label>
                    <p className="text-sm font-medium">{eventData?.venue}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* Modal de configuración de asientos - Solo para administradores */}
        {isAdmin && (
          <SeatConfigModal
            isOpen={isSeatConfigModalOpen}
            onClose={() => setIsSeatConfigModalOpen(false)}
            eventId={eventId}
            eventTitle={eventData?.title}
            onSuccess={() => {
              // Recargar los tipos de asientos para actualizar el mapa
              refetchSeatTypes();
            }}
          />
        )}

        {/* Notificaciones */}
        <Toast
          isVisible={toast.isVisible}
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      </div>
    </RoleProtectedRoute>
  );
};

export default EventDetails; 